import { Check, Copy, Loader2, Terminal, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { type ClipboardEvent, useEffect, useRef, useState } from "react";

import { AnalyticsEvents, captureAnalyticsEvent } from "@/analytics";
import { useOrganization } from "@/client/backend/organization.tsx";
import { useCreateAPIKey } from "@/client/backend/api-key";
import {
  CreateDeviceButton,
  DeviceStreamPane,
  OnboardingShell,
  useOnboardingInstance,
} from "@/components/onboarding/shared";
import { Button } from "@/components/ui/button";
import { BuildLogs } from "@/components/BuildLogs";
import { useAttachedXcodeInstance } from "@/client/director/instance-connections";
import { DeviceIllustration } from "./DeviceIllustration";

// Onboarding that leads with a one-click device, then hands the user a
// ready-made prompt for their coding agent (Claude Code, Cursor, Codex, ...)
// that authenticates the CLI and builds their app onto the device on screen.
export function AgentPromptOnboarding() {
  const [organization] = useOrganization();
  // Device and credential state belongs to the organization that created it.
  return <OrganizationOnboarding key={organization === "loading" ? "loading" : organization.id} />;
}

function OrganizationOnboarding() {
  const [organization] = useOrganization();
  const { trigger: createToken } = useCreateAPIKey(
    organization === "loading" ? "loading" : organization.id,
  );
  const { ready, isCreating, platform, instance, createInstance } = useOnboardingInstance();

  const { data: attachedBuilder, error: attachmentError } = useAttachedXcodeInstance(
    platform === "ios" ? instance?.id : undefined,
  );
  const [builderId, setBuilderId] = useState<string>();
  useEffect(() => {
    // Keep the last build visible after the builder terminates or detaches.
    if (attachedBuilder) setBuilderId(attachedBuilder);
  }, [attachedBuilder]);

  const [apiKey, setApiKey] = useState<string>();
  const [apiKeyFailed, setApiKeyFailed] = useState(false);
  const [copied, setCopied] = useState<"agent_prompt" | "terminal_commands">();
  const creatingKeyRef = useRef(false);
  const apiKeyRef = useRef<string | undefined>(undefined);

  const createApiKey = async () => {
    // Retrying after a failed device creation must not mint a second key.
    if (apiKeyRef.current || creatingKeyRef.current) return;
    creatingKeyRef.current = true;
    setApiKeyFailed(false);
    try {
      const response = await createToken({
        description: "Onboarding CLI key",
        expirationMonths: 12,
      });
      if (response?.error || !response?.data?.token) {
        throw new Error(response?.error?.message ?? "Failed to create token");
      }
      apiKeyRef.current = response.data.token;
      setApiKey(response.data.token);
    } catch {
      setApiKeyFailed(true);
    } finally {
      creatingKeyRef.current = false;
    }
  };

  const start = (selectedPlatform: "ios" | "android") => {
    void createApiKey();
    void createInstance(selectedPlatform);
  };

  // The key placeholder keeps the prompt usable when key creation failed;
  // the agent will ask the user for it instead of silently stalling.
  const keyForPrompt = apiKeyFailed ? "<create one in Settings -> API Keys>" : apiKey;
  const runCommand =
    platform === "ios"
      ? instance
        ? `npx -y lim run --ios-id ${instance.id}`
        : undefined
      : "npx -y lim run";
  const prompt =
    keyForPrompt && runCommand
      ? [
          `Run my ${platform === "ios" ? "iOS" : "Android"} app on the Limrun cloud ${platform === "ios" ? "simulator" : "emulator"} I'm watching in my browser.`,
          "",
          `1. Authenticate the Limrun CLI once: npx -y lim set-api-key ${keyForPrompt}`,
          `2. In my app's project directory, run: ${runCommand}`,
          "",
          "That command builds the app remotely, then installs and launches it on my device. If the build fails, read the errors, fix the code, and rerun the same command until the app is running. If there is no app project here yet, it can scaffold a sample app.",
        ].join("\n")
      : undefined;

  const terminalCommands =
    apiKey && runCommand ? `npx -y lim set-api-key ${apiKey} &&\n${runCommand}` : undefined;

  const copyContent = async (kind: "agent_prompt" | "terminal_commands") => {
    const content = kind === "agent_prompt" ? prompt : terminalCommands;
    if (!content) return;
    await navigator.clipboard.writeText(content);
    captureAnalyticsEvent(AnalyticsEvents.ONBOARDING_INSTALL_COMMAND_COPIED, {
      command_type: kind,
      copy_method: "button",
    });
    setCopied(kind);
    setTimeout(() => setCopied(undefined), 2000);
  };

  const captureManualPromptCopy = (event: ClipboardEvent<HTMLElement>) => {
    const selection = window.getSelection()?.toString().trim();
    if (!selection || selection !== event.currentTarget.textContent?.trim()) return;
    captureAnalyticsEvent(AnalyticsEvents.ONBOARDING_INSTALL_COMMAND_COPIED, {
      command_type: "agent_prompt",
      copy_method: "manual_selection",
    });
  };

  const started = isCreating || instance !== undefined;

  if (!started) {
    return (
      <OnboardingShell centered>
        <div className="onboarding-intro w-full max-w-3xl">
          <div className="grid items-center gap-8 md:grid-cols-[1.2fr_1fr] md:gap-12">
            <div>
              <p className="mb-5 flex items-center gap-2.5 text-xs text-muted-foreground">
                <span aria-hidden className="h-px w-6 bg-brand" />
                Your first run
              </p>
              <h1 className="text-[32px] font-semibold leading-[1.16] tracking-[-0.035em] sm:text-[38px]">
                From code to
                <br />a live device.
              </h1>
              <p className="mt-5 max-w-xs text-sm leading-6 text-muted-foreground">
                Start a cloud device. Give your coding agent a prompt. Watch your app come to life,
                right here.
              </p>
              <div className="mt-8">
                <CreateDeviceButton disabled={!ready} onCreate={start} />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">iOS or Android. No local setup.</p>
            </div>
            <DeviceIllustration className="hidden w-full md:block" />
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-5 md:mt-14">
            <p className="text-xs text-muted-foreground">
              Bring your app and the agent you already use.
            </p>
            <Link
              to="/instances"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              View instances <ArrowUpRight aria-hidden className="size-3.5" />
            </Link>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell centered={false}>
      <div className="mx-auto grid min-h-0 w-full max-w-7xl min-w-0 flex-1 gap-8 overflow-y-auto lg:grid-cols-2 lg:gap-12 lg:overflow-hidden">
        <div className="min-w-0 lg:my-auto lg:max-h-full lg:overflow-y-auto">
          {builderId ? (
            <BuildLogs key={builderId} instanceId={builderId} compact />
          ) : (
            <>
              <p className="mb-5 flex items-center gap-2 text-xs text-muted-foreground">
                <Terminal aria-hidden className="size-4 text-brand" />
                Next, your agent
              </p>
              <h1 className="text-2xl font-semibold tracking-tight">Let’s get your app running.</h1>
              <p className="mt-3 leading-6 text-muted-foreground">
                Paste this into your coding agent, opened in your app’s repository. It will build
                and install your app on this device.
              </p>
            </>
          )}
          <details
            key={builderId ? "building" : "prompt"}
            open={builderId ? undefined : true}
            className="mt-6"
          >
            <summary
              className={builderId ? "cursor-pointer text-xs text-muted-foreground" : "hidden"}
            >
              Agent prompt
            </summary>
            <div className="mt-3 overflow-hidden rounded-lg border bg-card">
              <div className="flex items-center justify-between border-b px-4 py-2">
                <span className="text-xs text-muted-foreground">Agent prompt</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!prompt}
                  onClick={() => void copyContent("agent_prompt")}
                  className="h-7 gap-1.5 px-2 text-xs"
                  aria-label={copied === "agent_prompt" ? "Prompt copied" : "Copy prompt"}
                >
                  {copied === "agent_prompt" ? (
                    <Check className="size-3.5" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                  {copied === "agent_prompt" ? "Copied" : "Copy prompt"}
                </Button>
              </div>
              {prompt ? (
                <pre
                  className="whitespace-pre-wrap break-words p-5 font-mono text-sm leading-6 text-muted-foreground"
                  onCopy={captureManualPromptCopy}
                >
                  {prompt}
                </pre>
              ) : (
                <div role="status" className="flex items-center gap-2 p-4 text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Preparing your prompt...
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Prefer the terminal?</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!terminalCommands}
                onClick={() => void copyContent("terminal_commands")}
                className="h-7 gap-1.5 px-2 text-xs"
                aria-label={
                  copied === "terminal_commands"
                    ? "Terminal commands copied"
                    : "Copy terminal commands"
                }
              >
                {copied === "terminal_commands" ? (
                  <Check className="size-3.5" />
                ) : (
                  <Copy className="size-3.5" />
                )}
                {copied === "terminal_commands" ? "Copied" : "Copy commands"}
              </Button>
            </div>
          </details>
          {attachmentError && !builderId && (
            <p role="status" className="mt-4 text-xs text-muted-foreground">
              Could not check for an attached builder. Retrying automatically.
            </p>
          )}
        </div>
        <DeviceStreamPane platform={platform} instance={instance} />
      </div>
    </OnboardingShell>
  );
}
